import {
    Autocomplete,
    Box,
    Button,
    ClickAwayListener,
    FilterOptionsState,
    IconButton,
    Link,
    TextField,
    Typography,
    createFilterOptions
} from "@mui/material";

import { EditIcon } from "../icons";
import { Tag } from "../../models";
import { useIssueFilter } from "./contexts/IssueFilterContext";
import { useState } from "react";

type TagsControlProps = {
    options: Array<Tag>;
    selectedTagIds: Array<string>;
    onTagChange?: (tagIds: Array<string>) => void;
    createTag?: (tagName: string) => Promise<Tag>;
};

type NewTagOption = {
    name: string;
    inputValue: string;
};

type TagOptionType = Tag | NewTagOption;

const filter = createFilterOptions<TagOptionType>();

const isExistingTag = (option: TagOptionType): option is Tag => {
    return (option as Tag).id !== undefined;
};

export const TagsControl = (props: TagsControlProps) => {
    const { options, selectedTagIds, onTagChange, createTag } = props;
    const { filter: currentFilter, updateFilter } = useIssueFilter();
    const [isEditing, setIsEditing] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const isEditable = !!onTagChange && !!createTag;

    return (
        <ClickAwayListener onClickAway={(e) => {
            e.stopPropagation();
            setIsEditing(false);
        }}>
            <Box
                display="flex"
                gap={1}
                alignItems="center"
                sx={{
                    transition: "background-color 0.3s",
                    ":hover": {
                        backgroundColor: isEditable && !isEditing ?
                            theme => theme.palette.action.hover : undefined,
                        "& .MuiIconButton-root": {
                            opacity: isEditable && !isEditing ? 1 : 0,
                            transform: isEditable && !isEditing ? "translateX(0px)" : "translateX(10px)"
                        }
                    },
                    "& .MuiIconButton-root": {
                        opacity: 0,
                        transform: "translateX(10px)",
                        transition: "opacity 0.3s, transform 0.3s"
                    }
                }}
                py={0.25}
                onClick={isEditable ? (e) => {
                    e.stopPropagation();
                    setIsEditing(true);
                } : undefined}
            >
                <Typography variant="body2" fontWeight={500}>Tags:</Typography>
                {isEditing ? (
                    <Autocomplete
                        onClose={() => setIsEditing(false)}
                        size="small"
                        fullWidth
                        multiple
                        options={options}
                        inputValue={inputValue}
                        onInputChange={(event, newInputValue) => {
                            if (event) {
                                setInputValue(newInputValue);
                            }
                        }}
                        isOptionEqualToValue={(option, value) => {
                            if (isExistingTag(option) && isExistingTag(value)) {
                                return option.id === value.id;
                            }
                            return option.name === (value as NewTagOption).name;
                        }}
                        getOptionLabel={(option) => {
                            return option.name ?? "";
                        }}
                        filterOptions={(options: Array<TagOptionType>, params: FilterOptionsState<TagOptionType>) => {
                            const filtered = filter(options, params);
                            const { inputValue } = params;

                            // Suggest the creation of a new value
                            const isExisting = options.some((option) => inputValue === (option as Tag).name);
                            if (inputValue && !isExisting) {
                                filtered.push({
                                    inputValue,
                                    name: `${inputValue} (new tag)`,
                                });
                            }
                            // Display tags that are not already selected
                            return filtered.filter((option) => {
                                if (isExistingTag(option)) {
                                    return !selectedTagIds.includes(option.id);
                                } else {
                                    return true;
                                }
                            });
                        }}
                        value={options.filter((option) => selectedTagIds.includes(option.id))}
                        onChange={async (event, newValue) => {
                            const tagsToBeCreated = newValue.filter(tag => !isExistingTag(tag));
                            const createdTags = await Promise.all(tagsToBeCreated.map((tag) => createTag?.((tag as NewTagOption).inputValue)));
                            onTagChange?.(
                                newValue.map((tag) => {
                                    if (isExistingTag(tag)) {
                                        return tag.id;
                                    } else {
                                        return createdTags.find((createdTag) => createdTag?.name === (tag as NewTagOption).inputValue)?.id;
                                    }
                                }).filter((tagId) => tagId != undefined) as Array<string>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                size="small"
                                onBlur={() => {
                                    setIsEditing(false);
                                }}
                            />
                        )}
                        disableClearable
                    />
                ) : (
                    <Box
                        display="flex"
                        columnGap={1}
                        flexWrap="wrap"
                        alignItems="center"
                    >
                        {selectedTagIds.length > 0 ? selectedTagIds.map((tagId) => {
                            const tag = options.find((option) => option.id === tagId);
                            return (
                                <Link
                                    key={tagId}
                                    component={Button}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        updateFilter({
                                            ...currentFilter,
                                            issueId: undefined,
                                            assessmentId: undefined,
                                            tagIds: [tagId]
                                        });
                                    }}
                                    sx={{
                                        textTransform: "none",
                                        minWidth: "unset",
                                        p: 0,
                                    }}
                                    color="info.main"
                                    fontSize="0.875rem"
                                >
                                    {tag?.name}
                                </Link>
                            );
                        }) : (
                            <Typography variant="body2">
                                -
                            </Typography>
                        )}
                        {isEditable && (
                            <Box px={0.5}>
                                <IconButton size="small" onClick={() => setIsEditing(true)} sx={{ width: "1.25rem", height: "1.25rem" }}>
                                    <EditIcon sx={{ width: "1.25rem", height: "1.25rem" }} />
                                </IconButton>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </ClickAwayListener>
    );
};